import { useState, useEffect } from "react";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { Calendar as CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

function toISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function fromISO(s) {
  if (!s || typeof s !== "string") return undefined;
  const [y, m, d] = s.split("-").map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
}

// Date picker: calendario popover su desktop, input data NATIVO su dispositivi touch (iPhone/iPad).
// Il Popover di Radix dentro un Dialog non riceve gli eventi touch su iOS Safari, quindi su mobile
// usiamo <input type="date"> che è affidabile ovunque. value/onChange usano stringhe ISO "yyyy-MM-dd".
export default function DatePicker({ value, onChange, placeholder = "Seleziona data", disabled, className, id, ...rest }) {
  const [open, setOpen] = useState(false);
  const [isTouch, setIsTouch] = useState(false);
  useEffect(() => {
    if (typeof window !== "undefined" && window.matchMedia) {
      try { setIsTouch(window.matchMedia("(pointer: coarse)").matches); } catch { /* noop */ }
    }
  }, []);
  const selected = fromISO(value);

  if (isTouch) {
    return (
      <div className={cn("relative w-full", className)}>
        <CalendarIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 opacity-70" />
        <input
          type="date"
          id={id}
          disabled={disabled}
          value={value || ""}
          onChange={(e) => onChange(e.target.value || "")}
          data-testid={rest["data-testid"]}
          className={cn(
            "flex h-10 w-full appearance-none rounded-md border border-input bg-background py-2 pl-9 pr-3 text-sm",
            "ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
            "disabled:cursor-not-allowed disabled:opacity-50",
            !value && "text-muted-foreground"
          )}
        />
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          id={id}
          data-testid={rest["data-testid"]}
          className={cn("w-full justify-start text-left font-normal", !selected && "text-muted-foreground", className)}
        >
          <CalendarIcon className="mr-2 h-4 w-4 shrink-0 opacity-70" />
          {selected ? format(selected, "dd/MM/yyyy") : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          locale={it}
          selected={selected}
          defaultMonth={selected}
          onSelect={(d) => { if (d) { onChange(toISO(d)); setOpen(false); } }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}
